import WebGLCanvas from "../Utility/WebGL/WebglCanvas.js";
import WebglUtility from "../Utility/WebGL/WebglUtility.js";
import {CreateCanvasREGLCommand, CreateFrameBufferCommand, ExecuteREGLCommand} from "./ProjectERegl.js";
import CanvasInputHandler from "../Utility/Input/CanvasInputHandler.js";
import EventSystem from "../Utility/EventSystem.js";
import MouseAnimation from "./Effect/MouseAnimation.js";
import {MaskHighLight} from "./Effect/MaskHighlight.js";
import {CustomEventTypes} from "../Utility/UniversalType.js";
class ProjectE extends WebGLCanvas {
  constructor(config) {
    super(config.webgl_dom);
    this._texAspectRatio = 1;
    this._screenAspectRatio = 1;
    this._config = config;
    this.webglUtility = new WebglUtility();
    this.eventSystem = new EventSystem();
    this._blackColor = [0, 0, 0, 1];
    this.maskHighlight = new MaskHighLight(this._webglDom, config);
    this.mouseAnimation = new MouseAnimation(config.mouse_dom, 0.8, this.maskHighlight.IsMobileDevice);
    this.inputHandler = new CanvasInputHandler(this._webglDom, this.eventSystem);
    this.eventSystem.ListenToEvent(CustomEventTypes.MouseMoveEvent, this.OnMouseMoveEvent.bind(this));
    this.eventSystem.ListenToEvent(CustomEventTypes.MouseUpEvent, this.OnMouseUpEvent.bind(this));
    this.eventSystem.ListenToEvent(CustomEventTypes.MouseDownEvent, this.OnMouseDownEvent.bind(this));
    this.InitProcess(config);
  }
  async InitProcess(config) {
    await this.maskHighlight.CacheMaskTexture();
    await this.SetupWebglPipeline(config);
    this.DrawREGLCavnas();
  }
  async SetupWebglPipeline(config) {
    this._reglContext = await this.CreatREGLCanvas(this._webglDom);
    let maskMaterial = await this.webglUtility.PrepareREGLShader(config.vertex_path, config.mask_effect_frag_path);
    let easeMaterial = await this.webglUtility.PrepareREGLShader(config.vertex_path, config.ease_effect_frag_path);
    this.frameBufferTex = this._reglContext.texture({radius: 256});
    this.mouseTrackFrameBuffer = this._reglContext.framebuffer({
      radius: 256,
      depthStencil: false
    });
    this._planeVertex = this.webglUtility.GetPlaneVertex();
    let textureASet = this.maskHighlight.GetPairTexture(0);
    let textureBSet = this.maskHighlight.GetPairTexture(1);
    if (textureBSet == null)
      textureBSet = textureASet;
    this._FrontTexA = this._reglContext.texture({data: textureASet[0], flipY: true});
    this._HighlightTexA = this._reglContext.texture({data: textureASet[1], flipY: true});
    this._FrontTexB = this._reglContext.texture({data: textureASet[0], flipY: true});
    this._HighlightTexB = this._reglContext.texture({data: textureASet[1], flipY: true});
    this.UpdatePlaneVertex();
    this.reglCanvasCommand = CreateCanvasREGLCommand(this._reglContext, this.mouseTrackFrameBuffer, maskMaterial.vertex_shader, maskMaterial.fragment_shader, this.maskHighlight._noise_texture, this._FrontTexA, this._HighlightTexA, this._FrontTexB, this._HighlightTexB, this._planeVertex.a_uv, this.maskHighlight.maskTexType.scale, this.maskHighlight.maskTexType.mask_min_reveal_range, this.maskHighlight.IsMobileDevice ? 1 : 0, this._planeVertex.count);
    this.reglFrameBufferCommand = CreateFrameBufferCommand(this._reglContext, this.mouseTrackFrameBuffer, this.frameBufferTex, maskMaterial.vertex_shader, easeMaterial.fragment_shader, this.maskHighlight._noise_texture, this._planeVertex.a_uv, this.maskHighlight.maskTexType.scale, this.maskHighlight.maskTexType.mask_min_reveal_range, this._planeVertex.count);
  }
  DrawREGLCavnas() {
    let commandCommand = {
      position: this._planeVertex.a_position,
      textureAspectRatio: this._texAspectRatio,
      screenAspectRatio: this._screenAspectRatio,
      time: 0,
      mousePos: [-1, -1],
      mainColor: [0, 0, 0],
      isMouseEnable: this.mouseAnimation.TouchVisibility,
      textureLerpValue: this.maskHighlight.LerpValue
    };
    let self = this;
    this._reglContext.frame(({time}) => {
      let clipPos = this.ScreenPositionToClipSpace(this.maskHighlight.inputInteractionType.mouse_screenpos_x, this.maskHighlight.inputInteractionType.mouse_screenpos_y);
      this._reglContext.clear({
        color: this._blackColor,
        depth: 1
      });
      commandCommand.position = this._planeVertex.a_position;
      commandCommand.time = time;
      commandCommand.mousePos = [clipPos.x, clipPos.y];
      commandCommand.isMouseEnable = this.mouseAnimation.TouchVisibility;
      commandCommand.textureLerpValue = this.maskHighlight.LerpValue;
      commandCommand.textureAspectRatio = this._texAspectRatio;
      commandCommand.screenAspectRatio = this._screenAspectRatio;
      if (commandCommand.mainColor[0] < 1)
        commandCommand.mainColor[0] += 0.01;
      if (!this.maskHighlight.IsMobileDevice) {
        self.mouseTrackFrameBuffer.use(function() {
          ExecuteREGLCommand(self._reglContext, self.reglFrameBufferCommand, commandCommand);
          self.frameBufferTex({
            width: 256,
            height: 256,
            copy: true,
            flipY: true
          });
        });
      }
      ExecuteREGLCommand(this._reglContext, this.reglCanvasCommand, commandCommand);
      this.mouseAnimation.OnUpdate(time);
      this.maskHighlight.OnUpdate(time, this._FrontTexA, this._HighlightTexA, this._FrontTexB, this._HighlightTexB);
      this.time = time;
    });
  }
  UpdatePlaneVertex() {
    if (this._FrontTexA == null)
      return;
    this._screenAspectRatio = this._webglDom.width / this._webglDom.height;
    let originalAspectRatio = this._FrontTexA.width / this._FrontTexA.height;
    this._texAspectRatio = originalAspectRatio / this._screenAspectRatio;
    this._planeVertex = this.webglUtility.ApplyAspectRatioToPlane(this.webglUtility.GetPlaneVertex(), this._texAspectRatio);
  }
  AutoSetCanvasSize() {
    super.AutoSetCanvasSize();
    this.UpdatePlaneVertex();
  }
  LoadGIFLoad() {
    let gifPath = "./image/Project_E_logo.gif";
    let logoDom = document.querySelector(".logo_image");
    logoDom.src = gifPath;
  }
  OnMouseMoveEvent(e) {
    this.maskHighlight.OnMouseMoveEvent(e.mousePosition.x, e.mousePosition.y);
    let mouseOffset = 40;
    this.mouseAnimation.OnMouseMoveEvent(e.mousePosition.x + mouseOffset, this._webglDom.clientHeight - e.mousePosition.y + mouseOffset);
  }
  OnMouseDownEvent(e) {
    this.mouseAnimation.OnMouseDownEvent();
  }
  OnMouseUpEvent(e) {
    this.mouseAnimation.OnMouseUpEvent(this.time);
  }
}
export default ProjectE;
